/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
/* eslint-disable no-nested-ternary */

import React, {
    useEffect, useState, useRef, useCallback,
} from 'react';

import {
    Grid, Typography, LinearProgress, Slide,
} from '@material-ui/core';

import { makeStyles } from '@material-ui/core/styles';
import {
    bool, func, object, oneOfType, number, shape, arrayOf, string, node,
} from 'prop-types';
import { uid } from 'react-uid';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import useClickAway from '../useClickAway';

import CYOGroupsContainer from './CYOGroupsContainer';
import CYOGroup from './CYOGroup';
import CYOBandGroup from './CYOBandGroup';
import ConnectedGraphqlFoodAddToCart from '../../../GraphqlFoodAddToCart/GraphqlFoodAddToCart';
import AddonDialog from '../../../GraphqlAddon/AddonDialog';
import { getFeatureFlag } from '../../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';

const useStyles = makeStyles(({ palette, breakpoints, spacing }) => ({
    mainContainer: {
        marginTop: '72px',
    },
    productItemsContainer: {
        maxWidth: `${breakpoints.values.lg}px`,
        margin: 'auto',
        '& [class*="MuiCard-root"]': {
            overflow: 'visible',
        },
    },
    progressCartContainer: ({ display }) => ({
        position: 'sticky',
        display,
        bottom: '0',
        left: '0',
        width: '100%',
        zIndex: 9999,
        background: palette?.background?.paper || '#fff',
        overflow: 'hidden',
    }),
    innerProgressCartContainer: {
        maxWidth: `${breakpoints.values.lg}px`,
        margin: 'auto',
    },
    progressBarRoot: {
        height: '24px',
        borderRadius: '0px',
        backgroundColor: palette?.background?.paper || '#fff',
        margin: '0px',
        boxShadow: '0px 3px 7px -1px rgba(0, 0, 0, 0.75)',
    },
    progressBarPrimColor: {
        backgroundColor: palette?.success?.main || '#57B560',
    },
    itemNumbers: {
        margin: `${spacing(1)}px ${spacing(3)}px`,
    },
    bannerError: {
        color: palette?.error.light || 'rgb(220, 30, 30)',
        margin: `${spacing(1)}px ${spacing(3)}px`,
    },
    cartButtonContainer: {
        paddingTop: '15px',
        paddingRight: spacing(4),
        paddingBottom: spacing(4),
        maxWidth: '350px',
        marginLeft: 'auto',
        '& button': {
            borderRadius: '20px',
        },
    },
    overlay: {
        right: '0',
        bottom: '0',
        top: '0',
        left: '0',
        backgroundColor: 'rgba(47, 47, 47, 0.3)',
        zIndex: '1',
        height: '100%',
        position: 'absolute',
    },
}));

const DesktopCYOProductItemsVTwo = ({
    handleMiniCartModalClick,
    brandId,
    deliverySLA,
    hasWine,
    product,
    handleMobileAgeVerification,
    selectedItemId,
    totalProductSelected,
    maxSelectableForSku,
    compileSelectedItems,
    selectedAddons,

    cyoData,
    handleItemSelection,
    selectedItems,
    selectionError,
    clearError,
    handleDisabledInteraction,
    removeItem,
    hideMenu,
    handleSelectBand,
    handleRemoveBand,
    selectedBand,
    getPrettyGroupName,
    useGuided,
    activeIndex,
    addonContainer,
    hasAddons,
}) => {
    const [cartDisplay, setCartDisplay] = useState('none');
    const [addScroll, setAddScroll] = useState(false);
    const [topOffset, setTopOffset] = useState();
    const cyoAddonEnabled =  useSelector(getFeatureFlag('is-cyo-addon-enabled'));
    const location = useLocation();

    // Override if one item is selected
    const classes = useStyles({
        display: totalProductSelected > 0 ? 'block' : cartDisplay,
    });
    const containerRef = useRef();
    const addonRef = useRef();
    const visibleGroups = cyoData.cyoGroup.filter((g) => g.type !== 'cyoNonCountable');
    const bandName = cyoData.cyoGroup[0]?.type === 'cyoBands' && cyoData.cyoGroup[0].name;

    // Only show the progress/cart section if scrolled to it.
    const scrollCallback = useCallback(() => {
        if (window.pageYOffset > topOffset) {
            setCartDisplay('block');
        } else {
            setCartDisplay('none');
        }
    }, [containerRef, topOffset]);

    useEffect(() => {
        if (document && typeof window !== 'undefined' && containerRef.current && addScroll) {
            document.addEventListener('scroll', scrollCallback);
        }
        return () => {
            if (document) {
                document.removeEventListener('scroll', scrollCallback);
            }
        };
    }, [containerRef, addScroll]);

    // Get the heights of containers
    useEffect(() => {
        if (containerRef.current) {
            // Give a little margin on top to show buttons
            const offsetTop = containerRef.current.offsetTop - 400 > 0 ? containerRef.current.offsetTop - 400 : 0;
            setTopOffset(offsetTop);
            setAddScroll(true);
        }
    }, [containerRef]);

    // Need to remove overflow on root for sticky to function
    useEffect(() => {
        if (typeof window !== 'undefined' && document && document.querySelector('#root')) {
            document.querySelector('#root').setAttribute('style', 'overflow: unset;');
        }
    }, []);

    let scrollElement = false;
    if (typeof window !== 'undefined') {
        scrollElement = window;
    }
    const { ref, active, toggle } = useClickAway();

    useEffect(() => {
        if (totalProductSelected && maxSelectableForSku === totalProductSelected) {
            toggle();
        }
    }, [maxSelectableForSku, totalProductSelected]);
    return (
        <div ref={containerRef} className={classes.mainContainer} data-testid="cyoDesktopItems">
            <CYOGroupsContainer
                Container={Grid}
                containerProps={{ container: true, className: classes.productItemsContainer }}
                hideMenu={hideMenu}
                forcedIndex={activeIndex}
                scrollElement={scrollElement}
                error={selectionError?.bannerError}
            >
                <div className={active && cyoAddonEnabled && hasAddons  ? `${classes.overlay}` : ''} />
                {visibleGroups.map((group, i) => {
                    const prettyGroupName = getPrettyGroupName(group.name);
                    // TODO: simplify ternary
                    const previousItemComplete = i === 0
                        ? true
                        : (useGuided
                            ?  !!selectedItems[visibleGroups[i - 1].name]?.completed || selectedBand?.groupName === visibleGroups[i - 1].name
                            : (bandName
                                ? selectedBand?.groupName === visibleGroups[0].name
                                : true
                            )
                        );
                    return (group.type === 'cyoBands' ? (
                        <CYOBandGroup
                            key={uid(prettyGroupName)}
                            handleSelectBand={handleSelectBand}
                            handleRemoveBand={handleRemoveBand}
                            selectedBandID={selectedBand.itemID}

                            groupData={group}
                            selectionError={selectionError}
                            clearError={clearError}
                        />
                    ) : (
                        <CYOGroup
                            groupData={group}
                            key={uid(group.name)}
                            selectedItems={selectedItems[group.name] || {}}
                            handleItemSelection={handleItemSelection}
                            selectionError={selectionError}
                            handleDisabledInteraction={handleDisabledInteraction}
                            clearError={clearError}
                            removeItem={removeItem}
                            data-name={prettyGroupName}
                            disabled={!previousItemComplete}
                            showYourSelection={!!bandName && i === 1}
                            selectionNumber={maxSelectableForSku}
                        />
                    ));
                })}

                <Grid data-testid="desktopCYOProgress" item xs={12}  ref={addonRef} container className={classes.progressCartContainer}>
                    { cyoAddonEnabled && hasAddons ? (
                        <Slide direction="up" in={active}  unmountOnExit  container={addonRef.current}>
                            <div
                                className={classes.addonDialog}
                                ref={ref}
                            >
                                <AddonDialog
                                    closeDialog={toggle}
                                    addonContainer={addonContainer}
                                />
                            </div>
                        </Slide>
                    ) : ''}
                    <Grid item xs={12} container className={classes.innerProgressCartContainer}>
                        <Grid item xs={12}>
                            <LinearProgress
                                classes={{
                                    root: classes.progressBarRoot,
                                    barColorPrimary: classes.progressBarPrimColor,
                                }}
                                variant="determinate"
                                value={totalProductSelected === 0 ? 0 : Math.round((totalProductSelected / maxSelectableForSku) * 100)}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            {
                                selectionError?.bannerError ? (
                                    <Typography className={classes.bannerError}>
                                        {selectionError.bannerError}
                                    </Typography>
                                ) : (
                                    <Typography className={classes.itemNumbers}>
                                        {`${totalProductSelected} of ${maxSelectableForSku} Items Selected`}
                                    </Typography>
                                )
                            }
                        </Grid>
                        <Grid item xs={4} />
                        <Grid justify="flex-end" item xs={4} className={classes.cartButtonContainer}>
                            <ConnectedGraphqlFoodAddToCart
                                handleMiniCartModalClick={handleMiniCartModalClick}
                                selectedItemId={selectedItemId}
                                brandId={brandId}
                                productType="cyo"
                                disableAddToCart
                                totalProductSelected={totalProductSelected}
                                maxComponents={maxSelectableForSku}
                                /*
                                    FORMAT:
                                    {
                                        brandCode: brandId,
                                        productCode: id,
                                        productId: legacyId,
                                        quantity: number,
                                        componentType: 'CYO'
                                    }
                                */
                                cyoSelectedComponents={compileSelectedItems()}
                                partNumber={product.partNumber}
                                product={product}
                                // If this is a band product, use functionality, otherwise do not
                                isBandItemSelected={bandName ? !!selectedBand.groupName : true}
                                hasWine={hasWine}
                                handleMobileAgeVerification={handleMobileAgeVerification}
                                deliveryMethod={deliverySLA}
                                deliveryIndicator=""
                                addons={selectedAddons}
                                categoryId={location?.state?.categoryId || ''}
                                categoryPath={location?.state?.categoryPath || ''}
                                categoryName={location?.state?.categoryName || ''}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </CYOGroupsContainer>

        </div>
    );
};

DesktopCYOProductItemsVTwo.propTypes = {
    brandId: string.isRequired,
    handleMiniCartModalClick: func.isRequired,
    deliverySLA: string.isRequired,
    product: object.isRequired,
    handleMobileAgeVerification: func.isRequired,
    totalProductSelected: number.isRequired,
    maxSelectableForSku: number.isRequired,
    cyoData: shape({
        maxQuantity: number.isRequired,
        cyoGroup: arrayOf(
            shape({
                name: string.isRequired,
                components: arrayOf(
                    shape({
                        id: string.isRequired,
                        legacyId: string.isRequired,
                        name: string.isRequired,
                        image: shape({
                            name: string.isRequired,
                            path: string.isRequired,
                        }),
                    }),
                ).isRequired,
            }),
        ).isRequired,
    }).isRequired,
    handleItemSelection: func.isRequired,
    selectedItems: object.isRequired,
    selectionError: object.isRequired,
    clearError: func.isRequired,
    handleDisabledInteraction: func.isRequired,
    removeItem: func.isRequired,
    handleSelectBand: func.isRequired,
    handleRemoveBand: func.isRequired,
    selectedBand: object.isRequired,
    compileSelectedItems: func.isRequired,
    selectedAddons: arrayOf(shape({
        brandCode: string,
        productCode: string,
        componentType: string,
        productId: string,
        quantity: number,
    })),
    hasWine: bool,
    selectedItemId: string,
    getPrettyGroupName: func,
    hideMenu: bool,
    useGuided: bool,
    activeIndex: oneOfType([number, bool]),
    addonContainer: node,
    hasAddons: bool,
};

DesktopCYOProductItemsVTwo.defaultProps = {
    /*
        The below '-' character is a special '-' character. The '-' coming from the CMS, was not matching
        my native '-' character, so I pulled it from the console, and it turns out the CMS is delivering a
        different Unicode version of '-'. I imagine it is an OS thing. I got that character from a unicode
        site, so be weary if you change this code. If the slicing stops working, it may be because your machine
        replaced this special '-' with a non-special '-'.
        Special: '–'
        Not:     '-'
        TODO: Figure out how to make more robust
    */
    getPrettyGroupName: (name) => (name.indexOf('–') > 0 ? name.slice(0, name.indexOf('–') - 1) : name),
    hideMenu: false,
    useGuided: true,
    activeIndex: false,
    selectedItemId: null,
    hasWine: false,
    selectedAddons: [],
    addonContainer: () => null,
    hasAddons: false,
};

export default DesktopCYOProductItemsVTwo;
